import { Box, Divider, Grid, Typography } from "@mui/material";
import Flex from "../../../../components/Flex";
import ProfileViewer from "../../../../components/ProfileViewer";
import { EMERGENCY_CONTACT_RELATION_MAPPER } from "../../../../constants/provider/add-new-patient";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import { useGetPatient } from "../../../../queries/use-get-patient-uuid";
import { formatDate } from "../../../../utils/date-time-utility";
import {
  DEFAULT_FONTSIZE,
  DEFAULT_USER_SRC,
  formatPhoneNumber,
  getAddressFormat,
  replaceUnderScoreAddSpace,
} from "../../../../utils/utils";
import { capitalize } from "lodash";

const getLanguagesText = (
  languages?: string[] | Record<string, string> | null
): string => {
  if (!languages) return "";

  if (Array.isArray(languages)) {
    return languages.join(", ");
  }

  return Object.values(languages).join(", ");
};

const PatientPortalProfile = () => {
  const [patient] = useCurrentUser();
  const { data: profileData } = useGetPatient(patient?.uuid);

  const fullName =
    `${profileData?.firstName ?? ""} ${profileData?.lastName ?? ""}`.trim();
  const contactNumber = formatPhoneNumber(
    profileData?.mobileNumber || profileData?.phone || ""
  );
  const DOB = profileData?.birthDate
    ? formatDate(profileData?.birthDate) || ""
    : "";
  const address = profileData?.address
    ? getAddressFormat(profileData.address) || ""
    : "";
  const languages = getLanguagesText(profileData?.languages);

  const emergencyContactDetails = profileData?.emergencyContacts;

  return (
    <Flex flexDirection={"column"} gap={3} width={"100%"}>
      <Box>
        <Typography fontWeight={"medium"} mb={1}>
          Profile Details
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3} md={1.5}>
            <Flex
              justifyContent={"center"}
              alignItems={"center"}
              borderRadius={"10px"}
              width={"100%"}
              height={"100%"}
            >
              <img
                style={{
                  width: "150px",
                  height: "150px",
                  objectFit: "cover",
                  borderRadius: "18px",
                }}
                alt="profile-image"
                src={profileData?.avatar || DEFAULT_USER_SRC}
              />
            </Flex>
          </Grid>
          <Grid item xs={12} sm={9} md={10}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ProfileViewer
                  data={{
                    name: fullName,
                    "contact number": contactNumber,
                    DOB,
                    race:
                      replaceUnderScoreAddSpace(
                        capitalize(profileData?.race)
                      ) || "",
                    address: address,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ProfileViewer
                  data={{
                    "email id": profileData?.email || "",
                    gender: profileData?.gender || "",
                    languages: languages,
                    ethnicity:
                      replaceUnderScoreAddSpace(
                        capitalize(profileData?.ethnicity)
                      ) || "",
                    SSN: profileData?.ssn || "",
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {emergencyContactDetails && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography fontWeight={"medium"} mb={2}>
            Emergency Contact Details
          </Typography>
          <Flex gap={2} flexDirection={"column"}>
            {emergencyContactDetails.map((contact: any, index: number) => (
              <Flex key={index} gap={2} flexWrap={"wrap"}>
                {EMERGENCY_CONTACT_KEYS.map((key) => {
                  const value =
                    key.value === "relationshipWithPatient"
                      ? EMERGENCY_CONTACT_RELATION_MAPPER[contact[key.value]]
                      : key?.value2
                        ? `${contact[key.value]} ${contact[key.value2]}`
                        : contact[key.value];
                  return (
                    <Flex key={key.value} gap={0.5} flex={1} minWidth={"180px"}>
                      <Typography
                        fontWeight={"medium"}
                        color={"#1A1A1A80"}
                        fontSize={DEFAULT_FONTSIZE}
                        whiteSpace={"nowrap"}
                      >
                        {key.label}
                      </Typography>
                      <Typography
                        fontSize={DEFAULT_FONTSIZE}
                        whiteSpace={"nowrap"}
                      >
                        : {value || ""}
                      </Typography>
                    </Flex>
                  );
                })}
              </Flex>
            ))}
          </Flex>
        </Box>
      )}
    </Flex>
  );
};

export default PatientPortalProfile;

const EMERGENCY_CONTACT_KEYS = [
  { value: "firstName", value2: "lastName", label: "Name" },
  { value: "mobile", label: "Contact Number" },
  { value: "email", label: "Email" },
  { value: "relationshipWithPatient", label: "Relationship" },
];
